/**
 * Client-side video compression, mirroring the one-off server script
 * (Eat2Live/video-compression/compressNew.js) but running in the browser
 * via ffmpeg.wasm, so videos are compressed before they are uploaded.
 *
 * Same encode settings as the server script:
 *   -vcodec libx264 -crf 28 -preset medium -acodec aac -b:a 128k -movflags +faststart
 *
 * As on the server, if the compressed result is not actually smaller the
 * original file is returned untouched.
 */
import { createFFmpeg, fetchFile } from '@ffmpeg/ffmpeg';

const CRF = 28;        // 18 visually lossless .. 28 strong compression
const PRESET = 'medium'; // x264 speed/size tradeoff

let ffmpeg = null;

const getFFmpeg = async () => {
  if (ffmpeg == null) {
    ffmpeg = createFFmpeg({
      log: false,
      corePath: `${window.location.origin}${process.env.PUBLIC_URL || ''}/ffmpeg/ffmpeg-core.js`,
    });
  }
  if (!ffmpeg.isLoaded()) {
    await ffmpeg.load();
  }
  return ffmpeg;
};

/**
 * @param {File} file          the picked video file
 * @param {(percent:number)=>void} onProgress  0..100, may be called many times
 * @returns {Promise<File>}    compressed file (or the original if not smaller)
 */
const compressVideo = async (file, onProgress) => {
  try {
    const ff = await getFFmpeg();

    if (onProgress) {
      ff.setProgress(({ ratio }) => {
        const pct = Math.round((ratio || 0) * 100);
        onProgress(Math.max(0, Math.min(100, pct)));
      });
    }

    const inName = 'input' + (file.name.match(/\.[^.]+$/)?.[0] || '.mp4');
    const outName = 'output.mp4';

    ff.FS('writeFile', inName, await fetchFile(file));

    await ff.run(
      '-i', inName,
      '-vcodec', 'libx264',
      '-crf', String(CRF),
      '-preset', PRESET,
      '-acodec', 'aac',
      '-b:a', '128k',
      '-movflags', '+faststart',
      outName,
    );

    const data = ff.FS('readFile', outName);

    // free the in-memory files before returning
    try { ff.FS('unlink', inName); } catch (e) { }
    try { ff.FS('unlink', outName); } catch (e) { }

    console.log('video size before: ' + file.size + ', after: ' + data.length);

    // Not actually smaller? keep the original, same as the server script.
    if (data.length >= file.size) {
      return file;
    }

    const outFileName = file.name.replace(/\.[^.]+$/, '') + '.mp4';
    return new File([data.buffer], outFileName, { type: 'video/mp4' });
  } catch (error) {
    // Never block the upload because compression failed — fall back to original.
    console.log('video compression failed, uploading original: ' + error);
    return file;
  }
};

export default compressVideo;
